import React, { Component } from 'react'
import { get } from 'lodash'
import { PropTypes } from 'prop-types'
import { Modal, ModalCTA } from 'containers/misc'
import { Title } from 'components/layout'
import { Button, ButtonGroup } from 'components/misc'
import { Input, Textarea, Checkbox } from 'components/fields'
import { ConfigCodeList } from 'components/sections/configurations'
import {
  CONFIGURATION_TYPES,
  SUPPORTED_FILE_TYPES,
  CONFIGURATION_KEYS,
  SUPPORTED_IMAGE_FILE_TYPES,
} from 'constants/enums'
import { getMin } from '../../../helpers/configKeySetHelper'

export default class ConfigurationKeySetModal extends Component {
  static propTypes = {
    modalId: PropTypes.string.isRequired,
    onUpdateConfigKey: PropTypes.func.isRequired,
    modal: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    onResetConfigKey: PropTypes.func.isRequired,
    isConfigUpdating: PropTypes.bool
  }

  static defaultProps = {
    data: {}
  }

  state = {
    isRemoving: false
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.props.initialize(this.props.data)
    }
  }

  componentWillReceiveProps(nextProps = {}) {
    const { data } = this.props
    if (data.id !== nextProps.data.id) {
      this.props.initialize(nextProps.data)
    }
  }

  handleRenderBoolean = data => {
    return (
      <Checkbox
        label={get(data, 'configurationKey.translatedDescription')}
        name="booleanValue"
      />
    )
  }

  handleRenderInteger = data => {
    return (
      <Input
        label={get(data, 'configurationKey.translatedDescription')}
        name="intValue"
        type="number"
        min={getMin(get(data, 'configurationKey.keyName.value'))}
      />
    )
  }

  handleRenderDecimal = data => {
    return (
      <Input
        label={get(data, 'configurationKey.translatedDescription')}
        name="decimalValue"
        type="number"
        step={0.01}
      />
    )
  }

  handleRenderText = data => {
    return (
      <Textarea
        onInputChange={this.disableSubmitOnEnter}
        label={get(data, 'configurationKey.translatedDescription')}
        name="textValue"
      />
    )
  }

  handleRenderBlob = data => {
    let accept
    // Set accepted file types based on the specific configuration key
    switch (get(data, 'configurationKey.keyName.value')) {
      case CONFIGURATION_KEYS.manualImageTransactionPayment.id:
        accept = SUPPORTED_IMAGE_FILE_TYPES
        break
      default:
        accept = SUPPORTED_FILE_TYPES
    }

    return (
      <div className="base-margin--bottom">
        <div>
          <span className="control-label">
            {get(data, 'configurationKey.translatedDescription')}
          </span>
        </div>
        <input
          type="file"
          onChange={this.handleFileSelection}
          accept={accept}
        />
      </div>
    )
  }

  handleRenderTextTranslated = data => {
    return (
      <Textarea
        label={get(data, 'configurationKey.translatedDescription')}
        name="translatedText"
      />
    )
  }

  handleRenderCodeListValueTranslated = data => {
    return <ConfigCodeList data={data} {...this.props} />
  }

  handleRequestClose = () => {
    this.props.modal(null)
    this.props.reset()
    this.setState({
      isRemoving: false
    })
  }

  handleFileSelection = e => {
    const file = e.target.files[0]

    this.props.change('file', file)
  }

  handleResetConfigValue = () => {
    this.setState({
      isRemoving: true
    })
  }

  handleResetConfigValueEnd = () => {
    this.setState({
      isRemoving: false
    })
  }

  handleUpdateKey = fields => {
    const { data } = this.props

    this.props
      .onUpdateConfigKey(fields, {
        type: get(data, 'configurationKey.valueType'),
        key: get(data, 'configurationKey.keyName.value')
      })
      .catch(this.handleResetConfigValueEnd)
  }

  handleResetConfigKey = () => {
    const { data } = this.props

    this.props
      .onResetConfigKey({
        type: get(data, 'configurationKey.valueType'),
        key: get(data, 'configurationKey.keyName.value')
      })
      .then(() => {
        this.handleResetConfigValueEnd()
      })
  }

  render() {
    const { modalId, data, handleSubmit, isConfigUpdating } = this.props

    const { isRemoving } = this.state

    let content

    const name = get(data, 'configurationKey.keyName.translationText')

    switch (get(data, 'configurationKey.valueType')) {
      case CONFIGURATION_TYPES.boolean.id:
        content = this.handleRenderBoolean(data)
        break
      case CONFIGURATION_TYPES.integer.id:
        content = this.handleRenderInteger(data)
        break
      case CONFIGURATION_TYPES.decimal.id:
        content = this.handleRenderDecimal(data)
        break
      case CONFIGURATION_TYPES.text.id:
        content = this.handleRenderText(data)
        break
      case CONFIGURATION_TYPES.blob.id:
        content = this.handleRenderBlob(data)
        break
      case CONFIGURATION_TYPES.textTranslated.id:
        content = this.handleRenderTextTranslated(data)
        break
      case CONFIGURATION_TYPES.codeListValueTranslated.id:
        content = this.handleRenderCodeListValueTranslated(data)
        break
      default:
        content = null
    }

    return (
      <Modal id={modalId} customActions enableOverflow>
        <Title h3 intlKey="moduleTitle.update_configuration_value">
          Update configuration key
        </Title>
        <form onSubmit={handleSubmit(this.handleUpdateKey)}>
          {content}
          <div className="base-margin--bottom" style={{ marginTop: '-15px' }}>
            <small>
              {name}
            </small>
          </div>
          <div className="row">
            <div className="col-xs-6">
              <ButtonGroup>
                <Button
                  type="button"
                  secondary
                  intlKey="button.close"
                  onClick={this.handleRequestClose}
                >
                  Close
                </Button>

                <Button
                  type="button"
                  negative
                  intlKey="button.reset"
                  onClick={this.handleResetConfigValue}
                >
                  Reset
                </Button>
              </ButtonGroup>
            </div>
            <div className="col-xs-6 text--right">
              <Button
                intlKey="button.update"
                isLoading={isConfigUpdating}
                disabled={isConfigUpdating}
              >
                Update
              </Button>
            </div>
          </div>
          {isRemoving && (
            <ModalCTA>
              <Title
                h2
                className="text--center"
                intlKey="moduleTitle.remove_customization_and_reset"
              >
                Remove customization and reset config value?
              </Title>
              <div className="row">
                <div className="col-xs-6">
                  <Button
                    type="button"
                    secondary
                    intlKey="button.cancel"
                    onClick={this.handleResetConfigValueEnd}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="col-xs-6 text--right">
                  <Button
                    negative
                    intlKey="button.confirm"
                    type="button"
                    isLoading={isConfigUpdating}
                    onClick={this.handleResetConfigKey}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </ModalCTA>
          )}
        </form>
      </Modal>
    )
  }
}
